/* eslint-disable @typescript-eslint/no-empty-interface */
import type { Tokens } from '@zeus/themes';

import {
  type Palette as MuiPalette,
  type PaletteColor as MuiPaletteColor,
  type ThemeOptions as MuiThemeOptions,
} from '@mui/material';

import { createComponents } from './createComponents';
import { createPalette } from './createPalette';
import { createShadows } from './createShadows';
import { createShape } from './createShape';
import { createSpacing } from './createSpacing';
import { createTypography } from './createTypography';
import { createUi } from './createUI';

/**
 * Serko's theme augmentations
 */

/**
 * @see {PaletteColor}
 */
interface CustomPaletteColor {
  darker: string;
  lighter: string;
}

/**
 * @see {Palette}
 */
interface CustomPalette {
  accent: {
    background: {
      '1': string;
      '2': string;
    };
    foreground: {
      '1': string;
      '2': string;
    };
  };
  brand: {
    dynamic: Omit<MuiPaletteColor, 'darker' | 'lighter'>;
    static: Omit<MuiPaletteColor, 'darker' | 'lighter'>;
  };
  forceWhite: MuiPaletteColor;
}

/**
 * @see {PaletteOptions}
 */
interface CustomPaletteOptions extends CustomPalette {}

/**
 * @see {ThemeOptions}
 */
interface CustomThemeOptions {
  palette: CustomPaletteOptions & Required<MuiPalette>;
  ui: Tokens['ui'];
}

/**
 * @see {Tokens}
 */
interface CustomTheme {
  palette: Required<CustomPaletteOptions>;
  ui: Tokens['ui'];
}

interface CustomButtonClasses {
  containedForceWhite: string;
  outlinedForceWhite: string;
  textForceWhite: string;
}

interface CustomTypeAction {
  focusOutline: string;
}

/**
 * @see https://mui.com/material-ui/customization/theming/#custom-variables
 */
declare module '@mui/material/styles' {
  export interface ThemeOptions extends CustomThemeOptions {
    shape?: {
      borderRadius?: number;
      borderRadiusMd?: number;
      borderRadiusLg?: number;
      borderRadiusFull?: number;
    };
  }

  export interface Theme extends CustomTheme {
    shape: {
      borderRadius: number;
      borderRadiusMd: number;
      borderRadiusLg: number;
      borderRadiusFull: number;
    };
  }
}

declare module '@mui/material/styles/createPalette' {
  export interface PaletteColor extends CustomPaletteColor {}
  export interface Palette extends CustomPalette {}
  export interface PaletteOptions extends CustomPaletteOptions {}
  export interface TypeAction extends CustomTypeAction {}
}

declare module '@mui/material/Button/buttonClasses' {
  export interface ButtonClasses extends CustomButtonClasses {}
}

export const createThemeOptions = (tokens: Tokens): MuiThemeOptions => {
  return {
    components: createComponents(tokens),
    palette: createPalette(tokens),
    shadows: createShadows(tokens),
    shape: createShape(tokens),
    spacing: createSpacing(tokens),
    typography: createTypography(tokens),
    ui: createUi(tokens),
  };
};
